import React from 'react';
import { TrashIcon } from '../assets/icons';
import dayjs from 'dayjs';
import { useLanguage } from '../contexts/LanguageContext';

const ScheduleDialog = ({
  isOpen,
  onClose,
  editingSchedule,
  scheduleForm,
  onScheduleChange,
  onSubmit,
  doctors,
  isSaving
}) => {
  const { t } = useLanguage();

  if (!isOpen) return null;

  const inputBase =
    'w-full px-3 py-2.5 text-gray-900 border border-gray-300 rounded-lg bg-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-shadow duration-200';
  const inputDisabled =
    'w-full px-3 py-2.5 border border-gray-200 rounded-lg bg-gray-50 text-gray-400';

  const handleFormInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    onScheduleChange({
      ...scheduleForm,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleDefaultScheduleChange = (e) => {
    const isDefault = e.target.checked;
    onScheduleChange({
      ...scheduleForm,
      is_default_schedule: isDefault,
      from_date: isDefault ? null : dayjs().format('YYYY-MM-DD'),
      to_date: isDefault ? null : dayjs().add(1, 'year').format('YYYY-MM-DD')
    });
  };

  const handleAddTimeSlot = (dayIndex) => {
    const updatedSchedule = { ...scheduleForm };
    if (!updatedSchedule.schedule[dayIndex]) {
      updatedSchedule.schedule[dayIndex] = [];
    }
    updatedSchedule.schedule[dayIndex].push({ from: '08:00', to: '18:00' });
    onScheduleChange(updatedSchedule);
  };

  const handleRemoveTimeSlot = (dayIndex, slotIndex) => {
    const updatedSchedule = { ...scheduleForm };
    updatedSchedule.schedule[dayIndex] = [
      ...updatedSchedule.schedule[dayIndex].slice(0, slotIndex),
      ...updatedSchedule.schedule[dayIndex].slice(slotIndex + 1)
    ];
    onScheduleChange(updatedSchedule);
  };

  const handleTimeSlotChange = (dayIndex, slotIndex, field, value) => {
    const updatedSchedule = { ...scheduleForm };
    updatedSchedule.schedule[dayIndex][slotIndex] = {
      ...updatedSchedule.schedule[dayIndex][slotIndex],
      [field]: value
    };
    onScheduleChange(updatedSchedule);
  };

  const handleApplyToAllDays = (fromDay) => {
    if (!window.confirm(t('scheduleDialog.confirmApplyToAllDays'))) {
      return;
    }

    const updatedSchedule = { ...scheduleForm };
    const sourceDaySlots = [...(updatedSchedule.schedule[fromDay] || [])].map((slot) => ({ ...slot }));

    for (const day of Object.keys(updatedSchedule.schedule)) {
      if (day !== fromDay && day !== 'step') {
        updatedSchedule.schedule[day] = [...sourceDaySlots];
      }
    }

    onScheduleChange(updatedSchedule);
  };

  const handleSortDaySlots = (dayIndex) => {
    const updatedSchedule = { ...scheduleForm };
    updatedSchedule.schedule[dayIndex] = [...updatedSchedule.schedule[dayIndex]]
      .sort((a, b) => a.from.localeCompare(b.from));
    onScheduleChange(updatedSchedule);
  };

  const dayLabels = [
    t('scheduleDialog.monday'),
    t('scheduleDialog.tuesday'),
    t('scheduleDialog.wednesday'),
    t('scheduleDialog.thursday'),
    t('scheduleDialog.friday'),
    t('scheduleDialog.saturday'),
    t('scheduleDialog.sunday')
  ];

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4">
      <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-xl bg-white shadow-xl">
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-gray-100 bg-white p-6 pb-4">
          <h3 className="text-xl font-semibold text-gray-900">
            {editingSchedule ? t('scheduleDialog.editSchedule') : t('scheduleDialog.addSchedule')}
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="-m-2 rounded-lg p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
            aria-label={t('common.cancel')}
          >
            ✕
          </button>
        </div>

        <form onSubmit={onSubmit} className="space-y-5 p-6">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="mb-1.5 block text-sm font-medium text-gray-700">
                {t('scheduleDialog.selectDoctor')}
              </label>
              <select
                name="doctor_id"
                value={scheduleForm.doctor_id || ''}
                onChange={handleFormInputChange}
                className={inputBase}
                required
              >
                <option value="">{t('scheduleDialog.pleaseSelectDoctor')}</option>
                {doctors.map((doctor) => (
                  <option key={doctor.id} value={doctor.id}>
                    {doctor.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-medium text-gray-700">
                {t('scheduleDialog.defaultSchedule')}
              </label>
              <label className="flex h-[46px] cursor-pointer items-center rounded-lg border border-gray-200 bg-gray-50 px-3">
                <input
                  type="checkbox"
                  id="is_default_schedule"
                  name="is_default_schedule"
                  checked={scheduleForm.is_default_schedule}
                  onChange={handleDefaultScheduleChange}
                  className="mr-2 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-600">{t('scheduleDialog.setAsDefault')}</span>
              </label>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="mb-1.5 block text-sm font-medium text-gray-700">
                {t('scheduleDialog.startDate')} {!scheduleForm.is_default_schedule && <span className="text-red-500">*</span>}
              </label>
              <div className="relative">
                <input
                  type="date"
                  name="from_date"
                  value={scheduleForm.from_date || ''}
                  onChange={handleFormInputChange}
                  className={scheduleForm.is_default_schedule ? inputDisabled : inputBase}
                  required={!scheduleForm.is_default_schedule}
                  disabled={scheduleForm.is_default_schedule}
                />
                {scheduleForm.is_default_schedule && (
                  <div className="pointer-events-none absolute inset-0 flex items-center justify-center rounded-lg bg-gray-50/90">
                    <span className="px-2 text-center text-xs text-gray-500">
                      {t('scheduleDialog.defaultScheduleNoDateRange')}
                    </span>
                  </div>
                )}
              </div>
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-medium text-gray-700">
                {t('scheduleDialog.endDate')} {!scheduleForm.is_default_schedule && <span className="text-red-500">*</span>}
              </label>
              <div className="relative">
                <input
                  type="date"
                  name="to_date"
                  value={scheduleForm.to_date || ''}
                  onChange={handleFormInputChange}
                  className={scheduleForm.is_default_schedule ? inputDisabled : inputBase}
                  required={!scheduleForm.is_default_schedule}
                  disabled={scheduleForm.is_default_schedule}
                />
                {scheduleForm.is_default_schedule && (
                  <div className="pointer-events-none absolute inset-0 flex items-center justify-center rounded-lg bg-gray-50/90">
                    <span className="px-2 text-center text-xs text-gray-500">
                      {t('scheduleDialog.defaultScheduleNoDateRange')}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div>
            <h5 className="mb-3 text-sm font-semibold text-gray-900">
              {t('scheduleDialog.weeklySchedule')}
            </h5>
            <div className="max-h-[50vh] space-y-3 overflow-y-auto pr-1">
              {dayLabels.map((day, index) => {
                const dayKey = index.toString();
                const slots = scheduleForm.schedule[dayKey] || [];
                return (
                  <div key={dayKey} className="rounded-xl border border-gray-200 bg-gray-50/40 p-3.5">
                    <div className="mb-2.5 flex flex-wrap items-center justify-between gap-2">
                      <h5 className="text-sm font-semibold text-gray-900">{day}</h5>
                      <div className="flex flex-wrap gap-2">
                        <button
                          type="button"
                          onClick={() => handleApplyToAllDays(dayKey)}
                          className="rounded-lg border border-gray-200 bg-white px-2.5 py-1 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-100"
                          title={t('scheduleDialog.applyToAllDays')}
                        >
                          {t('scheduleDialog.applyToAllDays')}
                        </button>
                        {slots.length > 1 && (
                          <button
                            type="button"
                            onClick={() => handleSortDaySlots(dayKey)}
                            className="rounded-lg border border-gray-200 bg-white px-2.5 py-1 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-100"
                            title={t('scheduleDialog.sortTimeSlots')}
                          >
                            {t('scheduleDialog.sortTimeSlots')}
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => handleAddTimeSlot(dayKey)}
                          className="rounded-lg bg-neutral-900 px-2.5 py-1 text-xs font-medium text-white transition-colors hover:bg-neutral-800"
                        >
                          {t('scheduleDialog.addTimeSlot')}
                        </button>
                      </div>
                    </div>

                    {slots.length > 0 ? (
                      <div className="space-y-2">
                        {slots.map((slot, slotIndex) => (
                          <div key={slotIndex} className="flex flex-wrap items-end gap-2 rounded-lg border border-gray-200 bg-white p-2.5">
                            <div className="min-w-[120px] flex-1">
                              <label className="mb-1 block text-xs font-medium text-gray-500">
                                {t('scheduleDialog.startTime')}
                              </label>
                              <input
                                type="time"
                                value={slot.from}
                                onChange={(e) => handleTimeSlotChange(dayKey, slotIndex, 'from', e.target.value)}
                                className={inputBase}
                              />
                            </div>
                            <div className="min-w-[120px] flex-1">
                              <label className="mb-1 block text-xs font-medium text-gray-500">
                                {t('scheduleDialog.endTime')}
                              </label>
                              <input
                                type="time"
                                value={slot.to}
                                onChange={(e) => handleTimeSlotChange(dayKey, slotIndex, 'to', e.target.value)}
                                className={inputBase}
                              />
                            </div>
                            <button
                              type="button"
                              onClick={() => handleRemoveTimeSlot(dayKey, slotIndex)}
                              className="rounded-lg p-2 text-red-500 transition-colors hover:bg-red-50 hover:text-red-700"
                              title={t('scheduleDialog.removeTimeSlot')}
                            >
                              <TrashIcon className="h-5 w-5" />
                            </button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="rounded-lg border border-dashed border-gray-200 bg-white px-3 py-4 text-sm italic text-gray-400">
                        {t('scheduleDialog.noScheduleForDay')}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          <div className="flex flex-wrap justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-gray-300 bg-white px-4 py-2.5 font-medium text-gray-700 transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              disabled={isSaving}
              className="rounded-lg bg-neutral-900 px-5 py-2.5 font-medium text-white shadow-sm transition-all duration-200 hover:bg-neutral-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isSaving ? t('common.processing') : editingSchedule ? t('scheduleDialog.updateSchedule') : t('scheduleDialog.createSchedule')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ScheduleDialog;
